// src/redux/defectsSlice.ts
// 不良品情報
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import apiAxios from "../utils/apiAxios";
import { AnyAction, Dispatch } from "redux";
import cloneDeep from "lodash/cloneDeep";
import { Pos } from "../models/Po";
import { GetWorkStatus } from "../models/GetWorkStatus";
import { DefectsTimingId } from "../models/GetDefectsReport";
import { GetDefectsReport } from "../models/GetDefectsReport";
import { PostDefectReport } from "../models/PostDefectReport";
import { appendAlert } from "./alertSlice";
import { toNumber } from "lodash";
import { GetDefectReportAttachements } from "../models/GetDefectReportAttachements";
import { PostDefectReportAttachement } from "../models/PostDefectReportAttachement";

/**
 * 工順選択オプションを表します
 *
 * @property mach_seq : 工順
 * @property mach_name : 機械名
 * @property proc_code : 加工コード
 * @property mach_code : 機械コード
 * @property current_qty : 計画数
 */
export type MachSeqOptions = {
  mach_seq: number;
  mach_name: string;
  proc_code: string;
  mach_code: string;
  current_qty: number;
};

/**
 * 不良情報を表します。
 *
 * @property rev : リビジョン
 * @property plantCode : 発見工場
 * @property procCode : 発見加工
 * @property machCode : 発見機械
 * @property po_data_num : 注文No
 * @property po_desc_num : 受注No
 * @property mach_seq : 工順
 * @property product_name : 商品名
 * @property order_dim_w : 受注寸法W
 * @property order_dim_h : 受注寸法H
 * @property currentQty : 計画数
 * @property inputErrors : エラーメッセージ
 */
export type DefectsInfoProp = {
  rev: number;
  plantCode: string;
  procCode: string;
  machCode: string;
  po_data_num: string;
  po_desc_num: string;
  mach_seq: number;
  product_name: string;
  order_dim_w: number;
  order_dim_h: number;
  currentQty: number;
  inputErrors: string[];
};

// 画像の状態　未登録 | 登録済み | 削除済み
type ImageStateProp = "unregistered" | "registered" | "deleted";

/**
 * 不良画像を表します。
 *
 * @property images : 画像データ(base64)
 * @property key : 削除キー
 * @property state : 状態
 */
export type DefectsImageProp = {
  images: string;
  key: string;
  state: ImageStateProp;
};

// 不良情報詳細
export type DefectsDetailProp = {
  isRegistered: boolean; // 登録済みか？
  defectsImages: DefectsImageProp[];
  plantCode: string;
  procCode: string;
  machCode: string;
  defect_type_seq: number;
  failure_count: number | null;
  detection_timing_id: DefectsTimingId; // 加工の前後
  worker_id: string;
  scrap_qty: number;
  defect_type_id: number;
  root_cause: string;
  corrective_action: string;
  inputErrors: string[]; // エラーメッセージ
};

export type UpdateDefectsDetailProp = {
  index: number;
  newDefectsDetail: DefectsDetailProp;
};

// 加工実績で選択した条件
export type ProcParams = {
  // 加工実績画面で選択した工場コード
  plantCode: string;
  // 加工実績画面で選択した加工コード
  procCode: string;
  // 加工実績画面で選択した機械コード
  machCode: string;
  // 加工実績画面の加工一覧 or 不良一覧で選択した受注コード
  poDescNum: string;
};

export type DefectsProp = {
  // 不良情報
  defectsInfo: DefectsInfoProp;
  // 不良情報詳細
  defectsDetails: DefectsDetailProp[];
  // 入力エラーが存在するか？
  isInputError: boolean;
  // 登録済みか
  isRegistered: boolean;
  // 工順オプション
  machSeqOptions: MachSeqOptions[];
  // 受注Noに対応する加工コードリスト
  procCodeMaster: string[];
  // 受注Noに対応する機械コードリスト
  machCodeMaster: string[];
  // 作業状況リスト(作業者選択用)
  workStatus: GetWorkStatus[];
  // 加工実績で選択した条件
  procParams: ProcParams;
};

const initialState: DefectsProp = {
  defectsInfo: {
    rev: 0,
    plantCode: "",
    procCode: "",
    machCode: "",
    po_data_num: "",
    po_desc_num: "",
    mach_seq: 0,
    product_name: "",
    order_dim_w: 0,
    order_dim_h: 0,
    currentQty: 0,
    inputErrors: [],
  },
  defectsDetails: [],
  isInputError: false,
  isRegistered: false,
  machSeqOptions: [],
  procCodeMaster: [],
  machCodeMaster: [],
  workStatus: [],
  procParams: {
    plantCode: "",
    procCode: "",
    machCode: "",
    poDescNum: "",
  },
};

export const defectsSlice = createSlice({
  name: "defects",
  initialState,
  reducers: {
    // 不良情報をセット
    setDefects: (state, action: PayloadAction<DefectsProp>) => {
      state.defectsInfo = action.payload.defectsInfo;
      state.defectsDetails = action.payload.defectsDetails;
      state.machSeqOptions = action.payload.machSeqOptions;
    },
    clearDefects: (state) => {
      // 不良情報をクリア
      state.defectsInfo = cloneDeep(initialState.defectsInfo);
      state.defectsDetails = cloneDeep(initialState.defectsDetails);
      state.machSeqOptions = cloneDeep(initialState.machSeqOptions);
      state.isInputError = false;
    },
    // 不良情報セット
    setDefectsInfo: (state, action: PayloadAction<DefectsInfoProp>) => {
      // 発見加工、発見機械の初期値をセットする
      // 加工実績一覧で選択した加工コード、機械コードが取得した加工実績のproc_recsに含まれている加工コード、機械コードに含まれるなら、それを初期値にする
      const workDefectsInfo = cloneDeep(action.payload);
      if (workDefectsInfo.plantCode === "" && state.procParams.plantCode) {
        workDefectsInfo.plantCode = state.procParams.plantCode;
      }
      if (state.procCodeMaster.includes(state.procParams.procCode)) {
        workDefectsInfo.procCode = state.procParams.procCode;
        if (state.machCodeMaster.includes(state.procParams.machCode)) {
          workDefectsInfo.machCode = state.procParams.machCode;
        }
      }
      // 工順を特定
      const machSeqOption = state.machSeqOptions.filter((d) => {
        return (
          d.proc_code === workDefectsInfo.procCode &&
          d.mach_code === workDefectsInfo.machCode
        );
      });
      if (machSeqOption.length > 0) {
        workDefectsInfo.mach_seq = cloneDeep(machSeqOption[0].mach_seq);
        workDefectsInfo.currentQty = cloneDeep(machSeqOption[0].current_qty);
      }
      // 不良情報を置き換え
      state.defectsInfo = workDefectsInfo;
    },
    // 発見加工コードをセット
    setProcCode: (state, action: PayloadAction<string>) => {
      state.defectsInfo.procCode = action.payload;
      // 加工コードを変更した場合は機械、工順、詳細をクリアする。
      state.defectsInfo.machCode = "";
      state.defectsInfo.mach_seq = 0;
      state.defectsInfo.currentQty = 0;
      state.defectsDetails = [];
    },
    // 発見機械コードをセット
    setMachCode: (state, action: PayloadAction<string>) => {
      state.defectsInfo.machCode = action.payload;
      // 工順を特定
      const machSeqOption = state.machSeqOptions.filter((d) => {
        return (
          d.proc_code === state.defectsInfo.procCode &&
          d.mach_code === state.defectsInfo.machCode
        );
      });
      // 工順が特定できた場合は工順と計画数をセット
      if (machSeqOption.length > 0) {
        state.defectsInfo.mach_seq = cloneDeep(machSeqOption[0].mach_seq);
        state.defectsInfo.currentQty = cloneDeep(machSeqOption[0].current_qty);
      } else {
        state.defectsInfo.mach_seq = 0;
        state.defectsInfo.currentQty = 0;
        state.defectsDetails = [];
      }
    },
    // 工順をセット
    setMachSeq: (state, action: PayloadAction<number>) => {
      state.defectsInfo.mach_seq = action.payload;
    },
    // 不良詳細をセット
    setDefectsDetails: (state, action: PayloadAction<DefectsDetailProp[]>) => {
      state.defectsDetails = cloneDeep(action.payload);
    },
    // 発生の前後をセット
    setDefectionTimingId: (
      state,
      action: PayloadAction<{
        detection_timing_id: DefectsTimingId;
        failure_count: number | null;
      }>
    ) => {
      // 同一failure_countのpositionをすべて更新する
      const newDefectsDetails = cloneDeep(state.defectsDetails);
      newDefectsDetails
        .filter((d) => d.failure_count === action.payload.failure_count)
        .forEach(
          (d) => (d.detection_timing_id = action.payload.detection_timing_id)
        );
      state.defectsDetails = newDefectsDetails;
    },
    // 新規の登録時
    setNewDefectsDetails: (state) => {
      // defect_type_seqは未登録の不良明細件数+1でセット
      const defectTypeSeq =
        state.defectsDetails.filter((d) => d.isRegistered === false).length + 1;
      state.defectsDetails = [
        ...state.defectsDetails,
        {
          isRegistered: false,
          defectsImages: [],
          plantCode: state.defectsInfo.plantCode,
          procCode: state.defectsInfo.procCode,
          machCode: state.defectsInfo.machCode,
          failure_count: null,
          detection_timing_id: 1,
          defect_type_seq: defectTypeSeq,
          worker_id: "",
          scrap_qty: 0,
          defect_type_id: 0,
          root_cause: "",
          corrective_action: "",
          inputErrors: [],
        },
      ];
    },
    // 不良詳細を削除
    removeDefectsDetails: (state, action: PayloadAction<number>) => {
      // 指定したインデックスの不良詳細を削除する
      const newDefectsDetail = cloneDeep(state.defectsDetails);
      newDefectsDetail.splice(action.payload, 1);
      // defect_type_seqを再設定
      newDefectsDetail
        .filter((d) => d.isRegistered === false)
        .forEach((d, index) => {
          d.defect_type_seq = index + 1;
        });

      state.defectsDetails = newDefectsDetail;
    },
    // 不良詳細を更新
    updateDefectDetail: (
      state,
      action: PayloadAction<UpdateDefectsDetailProp>
    ) => {
      // 指定したインデックスの配列を更新する
      let newDefectsDetails = cloneDeep(state.defectsDetails);
      newDefectsDetails[action.payload.index] = action.payload.newDefectsDetail;
      state.defectsDetails = newDefectsDetails;
    },
    // 工順選択リストデータセット
    setMachSeqOptions: (state, action: PayloadAction<MachSeqOptions[]>) => {
      state.machSeqOptions = cloneDeep(action.payload);
    },
    // 加工選択リストデータセット
    setProcCodeMaster: (state, action: PayloadAction<string[]>) => {
      state.procCodeMaster = cloneDeep(action.payload);
    },
    // 機械選択リストデータセット
    setMachCodeMaster: (state, action: PayloadAction<string[]>) => {
      state.machCodeMaster = cloneDeep(action.payload);
    },
    // 作業者選択リストデータセット
    setWorkStatus: (state, action: PayloadAction<GetWorkStatus[]>) => {
      state.workStatus = cloneDeep(action.payload);
    },
    // 登録済みか判定フラグをセット
    setIsRegistered: (state, action: PayloadAction<boolean>) => {
      state.isRegistered = action.payload;
    },
    // 不良情報取得用パラメータをセット
    setProcParams: (state, action: PayloadAction<ProcParams>) => {
      state.procParams = cloneDeep(action.payload);
    },
  },
});

const {
  setDefects,
  clearDefects,
  setDefectsInfo,
  setMachSeq,
  setProcCode,
  setMachCode,
  setDefectionTimingId,
  setDefectsDetails,
  setNewDefectsDetails,
  removeDefectsDetails,
  updateDefectDetail,
  setMachSeqOptions,
  setProcCodeMaster,
  setMachCodeMaster,
  setWorkStatus,
  setIsRegistered,
  setProcParams,
} = defectsSlice.actions;

/*
    取得した不良情報を画面用の仕様に合わせて変換
*/
const convertResponceToDefects = async (
  dispatch: Dispatch<AnyAction>,
  data: GetDefectsReport[]
) => {
  const defectsDetails: DefectsDetailProp[] = [];
  // 登録済みにフラグをセット
  dispatch(setIsRegistered(true));
  let first = true;
  for (const d of data) {
    if (first) {
      // 不良情報
      dispatch(
        setDefectsInfo({
          rev: d.rev,
          plantCode: d.plant_code,
          procCode: d.registering_proc_code,
          machCode: d.registering_mach_code,
          po_data_num: d.po_data_num,
          po_desc_num: d.po_desc_num,
          mach_seq: d.mach_seq,
          product_name: d.product_name,
          order_dim_w: d.order_dim_w,
          order_dim_h: d.order_dim_h,
          currentQty: d.order_qty,
          inputErrors: [],
        })
      );
    }
    first = false;

    // 画像取得
    const defect_report_attachements_url = `defect_report_attachements?po_data_num=${d.po_data_num}&po_desc_num=${d.po_desc_num}&mach_seq=${d.mach_seq}&failure_count=${d.failure_count}&defect_type_seq=${d.defect_type_seq}`;
    console.log(defect_report_attachements_url);
    const response = await apiAxios.get<GetDefectReportAttachements[]>(
      defect_report_attachements_url
    );
    let images: DefectsImageProp[] = [];
    if (response.data != null && response.data.length > 0) {
      // クエリパラメータから取得できるのは１件のみ想定
      images = response.data[0].files.map((d: any): DefectsImageProp => {
        return {
          images: d.base64_value,
          key: d.key,
          state: "registered",
        };
      });
    }
    // 不良情報詳細は配列でまとめる
    defectsDetails.push({
      isRegistered: true,
      defectsImages: images, //別途画像取得API
      plantCode: d.plant_code, // 製造工場コード
      procCode: d.occurred_proc_code, // 発生加工コード
      machCode: d.occurred_mach_code, // 発生機械作業コード
      worker_id: d.worker_id,
      failure_count: d.failure_count,
      detection_timing_id: d.detection_timing_id,
      defect_type_seq: d.defect_type_seq,
      scrap_qty: d.scrap_qty,
      defect_type_id: d.defect_type_id,
      root_cause: d.root_cause,
      corrective_action: d.corrective_action,
      inputErrors: [],
    });
  }
  // 不良情報詳細
  dispatch(setDefectsDetails(defectsDetails));
};

// 選択可能な加工コードリストを作成
// proc_listで取得した工順に含まれるコード
const createProcCodeMaster = (proc: Pos) => {
  if (proc.length === 0 || proc[0].po_descs.length === 0) {
    return [];
  }
  return proc[0].po_descs[0].proc_recs.map((p) => p.proc_code);
};

// 選択可能な機械コードリストを作成
// proc_listで取得した工順に含まれるコード
const createMachCodeMaster = (proc: Pos) => {
  if (proc.length === 0 || proc[0].po_descs.length === 0) {
    return [];
  }
  return proc[0].po_descs[0].proc_recs.map((p) => p.mach_code);
};

// 発見加工、発見機械に選択可能な項目は工順に存在するもののみとするため、加工実績から工順を取得
const fetchMachSeq = (dispatch: any, data: Pos) => {
  console.log(data);
  if (data !== undefined && data.length > 0) {
    if (data[0].po_descs[0].progress !== undefined) {
      const mach_seq_list: MachSeqOptions[] =
        data[0].po_descs[0].proc_recs?.map((d) => {
          return {
            mach_seq: d.mach_seq,
            mach_name: d.proc_name + " - " + d.mach_name,
            proc_code: d.proc_code,
            mach_code: d.mach_code,
            current_qty: d.current_qty,
          };
        });
      console.log(mach_seq_list);
      dispatch(setMachSeqOptions(mach_seq_list));
      dispatch(setProcCodeMaster(createProcCodeMaster(data)));
      dispatch(setMachCodeMaster(createMachCodeMaster(data)));
    } else {
      dispatch(setMachSeqOptions([]));
    }
  } else {
    dispatch(setMachSeqOptions([]));
  }
};

// 注文NOから加工実績を取得
const fetchProcs = (procParams: ProcParams) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    console.log(procParams.poDescNum);
    dispatch(clearDefects());
    // 受注Noが指定されている場合は不良情報＋加工実績取得
    if (procParams.poDescNum !== "") {
      // 加工実績取得
      try {
        const procListResponse = await apiAxios.get<Pos>(
          `proc_list?po_desc_num=${procParams.poDescNum}`
        );
        const procData = procListResponse.data;
        console.log(procData);
        // オプション選択用工順取得
        fetchMachSeq(dispatch, procData);
        if (procData.length > 0) {
          // 不良情報
          dispatch(
            setDefectsInfo({
              rev: procData[0].rev,
              plantCode: procParams.plantCode,
              procCode: procParams.procCode,
              machCode: procParams.machCode,
              po_data_num: procData[0].po_data_num,
              po_desc_num: procData[0].po_descs[0].po_desc_num,
              mach_seq: 0,
              product_name: procData[0].po_descs[0].product_name,
              order_dim_w: procData[0].po_descs[0].po_width,
              order_dim_h: procData[0].po_descs[0].po_height,
              currentQty: 0,
              inputErrors: [],
            })
          );
          // 新規なので空の詳細をひとつ追加
          dispatch(setDefectsDetails([]));
          dispatch(setNewDefectsDetails());
        } else {
          // 加工実績が取得できなかった受注Noはクリアしてやり直し
          dispatch(
            appendAlert({
              severity: "error",
              message:
                procParams.poDescNum + "の加工実績が取得できませんでした。",
              displayTime: 5000,
            })
          );
          const cloneParam = cloneDeep(procParams);
          cloneParam.poDescNum = "";
          dispatch(setProcParams(cloneParam));
        }
      } catch (error: any) {
        console.error(error);
        dispatch(
          appendAlert({
            severity: "error",
            message: "データの取得に失敗しました。" + error.message,
            displayTime: 5000,
          })
        );
      } finally {
      }
    }
  };
};

// 不良情報取得
const fetchDefects = (defectsInfo: DefectsInfoProp) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    // 不良実績を取得する
    console.log("不良実績を取得する");
    console.log(defectsInfo);
    const apiURL = `/defect_reports?plant_code=${defectsInfo.plantCode}&proc_code=${defectsInfo.procCode}&po_data_num=${defectsInfo.po_data_num}&po_desc_num=${defectsInfo.po_desc_num}&mach_code=${defectsInfo.machCode}&mach_seq=${defectsInfo.mach_seq}`;
    const defectsResponse = await apiAxios.get<GetDefectsReport[]>(apiURL);
    const defectData = defectsResponse.data;
    console.log(apiURL);
    console.log(defectData);
    // 本来は取得できないと[]で返ってくるがAPIの対応待ち
    if (defectData !== null && defectData.length > 0) {
      // 登録済みの不良情報が取得できた場合
      convertResponceToDefects(dispatch, defectData);
    } else {
      // 新規なので空の詳細をひとつ追加
      dispatch(setDefectsDetails([]));
      dispatch(setNewDefectsDetails());
    }
  };
};

// 加工コードに対応する作業者を取得
const feachWorkStatus = (plantCode: string, procCode: string) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    console.log(
      `/work_statuses?plant_code=${plantCode}&status=1,2,3&proc_code=${procCode}`
    );
    const response = await apiAxios.get<GetWorkStatus[]>(
      `/work_statuses?plant_code=${plantCode}&status=1,2,3&proc_code=${procCode}`
    );
    console.log(response.data);
    const work_status = response.data;
    // 対象がなしだとnullがかえってくるので暫定対処
    if (work_status !== null && work_status.length > 0) {
      dispatch(setWorkStatus(work_status));
    } else {
      dispatch(setWorkStatus([]));
    }
  };
};

// 入力チェック
const validateDefects = (
  dispatch: Dispatch<AnyAction>,
  defects_: DefectsProp
) => {
  const defects = cloneDeep(defects_);
  let isError = false;
  // 発見工程の入力チェック
  const defectsInfoErrors: string[] = [];
  if (defects.defectsInfo.procCode === "") {
    defectsInfoErrors.push("発見加工を選択してください。");
  }
  if (defects.defectsInfo.machCode === "") {
    defectsInfoErrors.push("発見機械を選択してください。");
  }
  defects.defectsInfo.inputErrors = defectsInfoErrors;

  if (defectsInfoErrors.length > 0) {
    isError = true;
  }

  // 不良詳細の入力チェック
  defects.defectsDetails.forEach((d) => {
    const defectDetailErrors: string[] = [];

    // 工順1かつ加工前の場合は発生加工、発生機械の未入力は許容する
    if (defects.defectsInfo.mach_seq !== 1 || d.detection_timing_id !== 1) {
      // 発生加工　入力されていること
      if (d.procCode === "") {
        defectDetailErrors.push("発生加工を選択してください。");
      }
      // 発生機械　入力されていること
      if (d.machCode === "") {
        defectDetailErrors.push("発生機械を選択してください。");
      }
    }

    if (d.worker_id === "") {
      defectDetailErrors.push("作業者を選択してください。");
    }
    // 不良品数　入力されていること
    if (d.scrap_qty === 0) {
      defectDetailErrors.push("不良品数を入力してください。");
    }
    if (defects.defectsInfo.currentQty < d.scrap_qty) {
      defectDetailErrors.push("不良品数の入力が計画数より多いです。");
    }
    // 不良理由　数値が入力されていること
    if (d.defect_type_id === 0) {
      defectDetailErrors.push("不良品理由を選択してください。");
    }
    // 発生原因 500byte以内
    if (new Blob([d.root_cause]).size > 500) {
      defectDetailErrors.push("発生原因は500byte以内で入力してください。");
    }
    // 対策 500byte以内
    if (new Blob([d.corrective_action]).size > 500) {
      defectDetailErrors.push("対策は500byte以内で入力してください。");
    }
    d.inputErrors = defectDetailErrors;
    if (defectDetailErrors.length > 0) {
      isError = true;
    }
  });

  // チェック結果を更新
  dispatch(setDefects(defects));
  if (isError) {
    dispatch(
      appendAlert({
        severity: "error",
        message: "入力項目を確認してください。",
        displayTime: 5000,
      })
    );
  }
  return isError;
};

// 不良登録
const postDefects = (defects: DefectsProp, userName: string) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    // 入力チェック
    const isError = await validateDefects(dispatch, defects);
    console.log(isError);
    if (!isError) {
      // 新規に追加する場合の不良発生回数(APIが対応していないので暫定で最大値+1を使用)
      const newFailureCount =
        Math.max(
          ...defects.defectsDetails.map((d) => {
            return d.failure_count !== null ? d.failure_count : 0;
          })
        ) + 1;

      // POST Defect用データ作成
      const defectsBodys = createPostDefectsData(
        defects,
        userName,
        newFailureCount
      );

      // 異なるfailureCountを一度にPOSTするとエラーとなるため暫定でfailureCount毎に分割してPOSTする
      const failureCountList = Array.from(
        new Set(defectsBodys.map((d) => d.failure_count))
      );
      for (const failureCount of failureCountList) {
        const body = defectsBodys.filter(
          (d) => d.failure_count === failureCount
        );
        console.log(body);
        try {
          const response = await apiAxios.post("/defect_reports", body);
          console.log(response);
        } catch (error: any) {
          dispatch(
            appendAlert({
              severity: "error",
              message: "不良実績登録エラー " + error.message,
              displayTime: 5000,
            })
          );
        }
      }

      // 画像登録
      for (const defectsDetail of defects.defectsDetails) {
        for (const defectsImage of defectsDetail.defectsImages) {
          // 未登録の場合のみPOSTする
          if (defectsImage.state === "unregistered") {
            const body: PostDefectReportAttachement[] = [
              {
                po_data_num: defects.defectsInfo.po_data_num,
                po_desc_num: defects.defectsInfo.po_desc_num,
                mach_seq: defects.defectsInfo.mach_seq,
                failure_count:
                  defectsDetail.failure_count === null
                    ? newFailureCount
                    : defectsDetail.failure_count,
                defect_type_seq: defectsDetail.defect_type_seq,
                base64_value: defectsImage.images,
                update_by: userName,
              },
            ];
            console.log(body);
            try {
              const response = await apiAxios.post<PostDefectReportAttachement>(
                "/defect_report_attachement",
                body
              );
              console.log(response);
            } catch (error: any) {
              dispatch(
                appendAlert({
                  severity: "error",
                  message: "不良実績画像登録エラー " + error.message,
                  displayTime: 5000,
                })
              );
            }
          }
        }
      }
      // 再取得
      try {
        const apiURL = `/defect_reports?plant_code=${defects.defectsInfo.plantCode}&proc_code=${defects.defectsInfo.procCode}&po_data_num=${defects.defectsInfo.po_data_num}&po_desc_num=${defects.defectsInfo.po_desc_num}&mach_code=${defects.defectsInfo.machCode}&mach_seq=${defects.defectsInfo.mach_seq}`;
        const defectsResponse = await apiAxios.get<GetDefectsReport[]>(apiURL);
        console.log(defectsResponse);
        const defectData = defectsResponse.data;
        if (defectData !== null && defectData.length > 0) {
          // 登録済みの不良情報が取得できた場合
          convertResponceToDefects(dispatch, defectData);
        } else {
          // 新規なので空の詳細をひとつ追加
          dispatch(setDefectsDetails([]));
          dispatch(setNewDefectsDetails());
        }
      } catch (error: any) {
        dispatch(
          appendAlert({
            severity: "error",
            message: "不良実績再取得エラー " + error.message,
            displayTime: 5000,
          })
        );
      }
      dispatch(
        appendAlert({
          severity: "success",
          message: "不良実績登録が完了しました。",
          displayTime: 5000,
        })
      );
    }
  };
};

// 不良情報登録
const createPostDefectsData = (
  defects: DefectsProp,
  userName: string,
  newFailureCount: number
): PostDefectReport[] => {
  const postDefectsData: PostDefectReport[] = [];

  defects.defectsDetails.forEach((d) => {
    postDefectsData.push({
      po_data_num: defects.defectsInfo.po_data_num,
      po_desc_num: defects.defectsInfo.po_desc_num,
      rev: defects.defectsInfo.rev,
      plant_code: defects.defectsInfo.plantCode,
      mach_seq: defects.defectsInfo.mach_seq,
      // 加工 　加工実績の加工(グローバル)あれば優先
      registering_proc_code:
        defects.procParams.procCode !== ""
          ? defects.procParams.procCode
          : defects.defectsInfo.procCode,
      // 機械　加工実績の機械(グローバル)あれば優先
      registering_mach_code:
        defects.procParams.machCode !== ""
          ? defects.procParams.machCode
          : defects.defectsInfo.machCode,
      worker_id: d.worker_id,
      failure_count:
        d.failure_count === null ? newFailureCount : d.failure_count, // 不良発生回数
      detection_timing_id: d.detection_timing_id, // 発見位置、加工前か加工後か
      defect_type_seq: d.defect_type_seq, // branch_id
      scrap_qty: d.scrap_qty, // 不良枚数
      occurred_proc_code: d.procCode, // 発生加工コード
      occurred_mach_code: d.machCode, // 発生機械作業コード
      defect_type_id: toNumber(d.defect_type_id), // 不良品理由区分
      root_cause: d.root_cause, // 発生原因
      corrective_action: d.corrective_action, // 対策
      update_by: userName, // DBの桁数少ないため暫定処置
    });
  });
  return postDefectsData;
};

// 不良画像を削除
const deleteDefectReports = (key: string) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    try {
      const url = `/defect_report_attachement/${key}`;
      const response = await apiAxios.delete(url);
      console.log(response);
      console.log("画像削除しました : " + key);
    } catch (error: any) {
      console.error(error);
      dispatch(
        appendAlert({
          severity: "error",
          message: "画像削除に失敗しました : " + key,
          displayTime: 5000,
        })
      );
    }
  };
};

export {
  fetchProcs,
  fetchDefects,
  feachWorkStatus,
  postDefects,
  deleteDefectReports,
  fetchMachSeq,
  setDefects,
  clearDefects,
  setDefectsDetails,
  setMachSeq,
  setProcCode,
  setMachCode,
  setDefectionTimingId,
  setNewDefectsDetails,
  updateDefectDetail,
  removeDefectsDetails,
  setProcParams,
};
export default defectsSlice.reducer;
